import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExternalLink, Navigation, Home, Check, X, Loader2 } from "lucide-react";

const PALETTE = ["#6366f1", "#ec4899", "#10b981", "#f59e0b", "#06b6d4", "#a855f7", "#ef4444", "#14b8a6"];

function colorFor(name, order) {
  const key = name || "—";
  const idx = order.indexOf(key);
  if (idx >= 0) return PALETTE[idx % PALETTE.length];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function pinIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
    popupAnchor: [0, -18],
  });
}

const geoCacheGet = (address) => {
  try { const h = localStorage.getItem("geo:" + address); return h ? JSON.parse(h) : null; } catch { return null; }
};
const geoCacheSet = (address, pt) => {
  try { localStorage.setItem("geo:" + address, JSON.stringify(pt)); } catch { /* ignore */ }
};

function fmtDrive(mins) {
  if (mins == null) return "";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const escapeHtml = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export default function ShootsMap({ shoots, team = [] }) {
  const { user } = useAuth();
  const canEdit = !user?.is_member;
  const [pts, setPts] = useState({}); // address -> {lat,lng}
  const [loading, setLoading] = useState(true);
  const [homes, setHomes] = useState({}); // photographer(lower) -> address
  const [editing, setEditing] = useState(null); // photographer name being edited
  const [homeDraft, setHomeDraft] = useState("");
  const [savingHome, setSavingHome] = useState(false);
  const [summaries, setSummaries] = useState({}); // photographer -> {miles,minutes,home}|{loading}|{error}
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  // Seed home bases from the dashboard team payload (editor home + photographer_homes).
  useEffect(() => {
    const m = {};
    (team || []).forEach((t) => { if (t.name && t.home_location) m[String(t.name).trim().toLowerCase()] = t.home_location; });
    setHomes(m);
  }, [team]);

  const photographers = useMemo(() => {
    const set = [];
    shoots.forEach((s) => (s.photographers && s.photographers.length ? s.photographers : ["—"]).forEach((p) => { if (!set.includes(p)) set.push(p); }));
    return set;
  }, [shoots]);

  // Initialise the Leaflet map once, with imperative cleanup (StrictMode safe).
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: false }).setView([39.5, -98.35], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Geocode addresses server-side (Nominatim with a proper User-Agent + address
  // variant fallbacks + DB cache); localStorage adds an instant layer for repeats.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const uniq = [...new Set(shoots.map((s) => s.address).filter(Boolean))];
      const out = {};
      const need = [];
      uniq.forEach((a) => { const c = geoCacheGet(a); if (c) out[a] = c; else need.push(a); });
      if (!cancelled && Object.keys(out).length) setPts({ ...out });
      if (need.length) {
        try {
          const { data } = await api.post("/geocode", { addresses: need });
          const res = (data && data.results) || {};
          Object.entries(res).forEach(([a, pt]) => { if (pt) { out[a] = pt; geoCacheSet(a, pt); } });
        } catch { /* ignore */ }
      }
      if (!cancelled) { setPts({ ...out }); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [shoots]);

  // Render markers whenever geocoded points or shoots change.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const latlngs = [];
    shoots.forEach((s) => {
      const p = pts[s.address];
      if (!p) return;
      const color = colorFor((s.photographers || [])[0] || "—", photographers);
      const marker = L.marker([p.lat, p.lng], { icon: pinIcon(color) });
      const html = `<div style="min-width:160px">
        <div style="font-weight:600">${escapeHtml(s.address)}</div>
        <div style="color:${color};font-weight:500">${escapeHtml((s.photographers || []).join(", ") || "—")}</div>
        <div style="color:#666">${escapeHtml(fmtTime(s.start_at))}</div>
      </div>`;
      marker.bindPopup(html);
      marker.on("mouseover", () => marker.openPopup());
      marker.on("mouseout", () => marker.closePopup());
      marker.addTo(layer);
      latlngs.push([p.lat, p.lng]);
    });
    if (latlngs.length === 1) map.setView(latlngs[0], 12);
    else if (latlngs.length > 1) map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 13 });
  }, [pts, shoots, photographers]);

  // Daily driving distance + time per photographer (home -> shoots -> home) via OSRM.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      for (const p of photographers) {
        const stops = shoots
          .filter((s) => (s.photographers || []).includes(p) && s.address)
          .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))
          .map((s) => s.address);
        const home = homes[String(p).trim().toLowerCase()];
        const ordered = home ? [home, ...stops, home] : stops;
        if (ordered.length < 2) { setSummaries((prev) => ({ ...prev, [p]: { error: true } })); continue; }
        setSummaries((prev) => ({ ...prev, [p]: { loading: true } }));
        try {
          const { data } = await api.post("/route-summary", { addresses: ordered });
          if (cancelled) return;
          setSummaries((prev) => ({ ...prev, [p]: data && data.ok ? { miles: data.miles, minutes: data.minutes, home: !!home } : { error: true } }));
        } catch {
          if (!cancelled) setSummaries((prev) => ({ ...prev, [p]: { error: true } }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [photographers, homes, loading, shoots]);

  const located = shoots.filter((s) => pts[s.address]);
  const unmapped = shoots.filter((s) => s.address && !pts[s.address]);

  const homeFor = (photog) => homes[String(photog || "").trim().toLowerCase()] || null;

  const openHomeEditor = (photog) => {
    setEditing(photog);
    setHomeDraft(homeFor(photog) || "");
  };

  const saveHome = async () => {
    setSavingHome(true);
    try {
      const { data } = await api.put("/photographer-homes", { name: editing, address: homeDraft.trim() });
      // Merge server truth (keyed lower-case) into local homes.
      setHomes((prev) => ({ ...prev, [String(editing).trim().toLowerCase()]: homeDraft.trim() || undefined, ...data.homes }));
      toast.success(homeDraft.trim() ? "Home base saved" : "Home base cleared");
      setEditing(null);
    } catch (e) {
      toast.error("Couldn't save home base");
    } finally { setSavingHome(false); }
  };

  const routeUrl = (photog) => {
    const stops = shoots
      .filter((s) => (s.photographers || []).includes(photog) && s.address)
      .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))
      .map((s) => encodeURIComponent(s.address));
    const home = homeFor(photog);
    const all = home ? [encodeURIComponent(home), ...stops, encodeURIComponent(home)] : stops;
    return "https://www.google.com/maps/dir/" + all.join("/");
  };

  return (
    <div data-testid="shoots-map">
      <div className="mb-3 flex flex-wrap gap-2">
        {photographers.map((p) => {
          const base = homeFor(p);
          return (
            <div key={p} className="inline-flex items-center gap-1 rounded-full border border-border pr-1">
              <a href={routeUrl(p)} target="_blank" rel="noopener noreferrer" data-testid={`route-${p}`}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors hover:bg-secondary"
                title={base ? `Route starts & ends at ${base}` : `Open ${p}'s route (no home base set)`}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorFor(p, photographers) }} />
                {p} <Navigation className="h-3 w-3 opacity-60" />
              </a>
              {canEdit && (
                <button onClick={() => openHomeEditor(p)} data-testid={`sethome-${p}`}
                  title={base ? `Home base: ${base} — click to edit` : "Set start/end home base"}
                  className={`rounded-full p-1 transition-colors ${base ? "text-emerald-500 hover:bg-emerald-500/10" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                  <Home className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="mb-3 flex flex-col gap-2 rounded-lg border border-border bg-secondary/30 p-2.5 sm:flex-row sm:items-center" data-testid="home-editor">
          <span className="text-xs text-muted-foreground">Start &amp; end base for <span className="font-medium text-foreground">{editing}</span>:</span>
          <input value={homeDraft} onChange={(e) => setHomeDraft(e.target.value)} data-testid="home-input"
            placeholder="123 Main St, City, ST" autoFocus
            className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-sm" />
          <div className="flex gap-1.5">
            <button onClick={saveHome} disabled={savingHome} data-testid="home-save"
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground">
              {savingHome ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
            </button>
            <button onClick={() => setEditing(null)} data-testid="home-cancel"
              className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}
      <div ref={containerRef} data-testid="shoots-map-canvas"
        className="h-[360px] w-full overflow-hidden rounded-lg border border-border" style={{ zIndex: 0 }} />
      <p className="mt-2 text-xs text-muted-foreground">
        {loading ? "Locating addresses…" : `${located.length} of ${shoots.length} shoots mapped.`} Click a photographer to open their route <ExternalLink className="inline h-3 w-3" />
      </p>
      {!loading && unmapped.length > 0 && (
        <div className="mt-1 text-xs text-amber-600 dark:text-amber-400" data-testid="shoots-unmapped">
          Couldn't locate {unmapped.length}: {unmapped.map((s) => s.address).join("  ·  ")}
        </div>
      )}
      {!loading && photographers.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-border pt-2" data-testid="distance-summary">
          <div className="overline text-muted-foreground">Daily drive</div>
          {photographers.map((p) => {
            const s = summaries[p];
            return (
              <div key={p} className="flex items-center gap-2 text-xs" data-testid={`drive-${p}`}>
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colorFor(p, photographers) }} />
                <span className="font-medium">{p}</span>
                {s?.loading ? <span className="text-muted-foreground">calculating…</span>
                  : s?.miles != null ? <span className="text-muted-foreground">{s.miles} mi · {fmtDrive(s.minutes)}{s.home ? "" : " · set home base for full loop"}</span>
                  : <span className="text-muted-foreground">—</span>}
              </div>
            );
          })}
        </div>
      )}
      {canEdit && (
        <p className="mt-1 text-xs text-muted-foreground">
          <Home className="mr-1 inline h-3 w-3" /> Set each photographer's home base (via the <Home className="inline h-3 w-3" /> icon) — routes will start &amp; end there so daily drive distances are complete.
        </p>
      )}
    </div>
  );
}
